import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, Loader2 } from "lucide-react";
import { usePaymentsConfig, loadPayPalSdk, money } from "@/components/PayPalCheckout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PayInstalment() {
  const { planId } = useParams();
  const config = usePaymentsConfig();
  const [plan, setPlan] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [state, setState] = useState("loading");
  const containerRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/payments/plan/${planId}`)
      .then((res) => setPlan(res.data))
      .catch(() => setNotFound(true));
  }, [planId]);

  const active = plan?.status === "active";

  useEffect(() => {
    if (!config?.configured || !active || state === "paid") return;
    let cancelled = false;
    loadPayPalSdk(config.client_id, config.currency || "USD")
      .then((paypal) => {
        if (cancelled || !containerRef.current) return;
        containerRef.current.replaceChildren();
        setState("ready");
        paypal
          .Buttons({
            style: { layout: "vertical", color: "black", shape: "rect", label: "paypal", height: 48 },
            createOrder: async () => {
              const { data } = await axios.post(`${API}/payments/paypal/order`, {
                package_id: plan.package_id,
                plan_id: plan.id,
              });
              return data.order_id;
            },
            onApprove: async (data) => {
              setState("processing");
              const res = await axios.post(`${API}/payments/paypal/capture`, { order_id: data.orderID });
              if (res.data.status === "paid") {
                setState("paid");
                toast.success("Instalment received — receipt on its way.");
                const { data: fresh } = await axios.get(`${API}/payments/plan/${planId}`);
                setPlan(fresh);
              } else {
                setState("ready");
                toast.error("PayPal is still processing this payment. We will email you once it clears.");
              }
            },
            onCancel: () => toast.info("Payment cancelled — nothing was charged."),
            onError: (err) => {
              console.error(err);
              setState("ready");
              toast.error("PayPal could not complete the payment. Please try again.");
            },
          })
          .render(containerRef.current)
          .catch(() => setState("error"));
      })
      .catch(() => setState("error"));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, active, planId]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-pine px-5 py-16" data-testid="pay-instalment-page">
      <div className="w-full max-w-lg border border-bone/10 bg-forest/30 p-8 md:p-12">
        <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.35em] text-terra">BookCare LLC · Instalment plan</p>

        {notFound ? (
          <div data-testid="plan-not-found">
            <h1 className="mb-4 font-serif text-3xl font-medium text-bone">Plan not found.</h1>
            <p className="mb-8 text-sm leading-relaxed text-bone/60">
              This payment link is not valid. Please check the link in your email, or contact
              Info@bookcarellc.com and we will resend it.
            </p>
            <Link to="/" className="font-mono text-xs uppercase tracking-[0.2em] text-bone/50 underline-offset-4 hover:text-bone hover:underline">
              ← Back to website
            </Link>
          </div>
        ) : !plan ? (
          <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-bone/40">
            <Loader2 size={13} className="animate-spin" /> Loading your plan…
          </p>
        ) : (
          <>
            <h1 className="mb-1 font-serif text-3xl font-medium leading-tight text-bone" data-testid="plan-label">
              Your payment plan
            </h1>
            <p className="mb-8 text-sm text-bone/60">{plan.label}</p>

            <div className="mb-3 flex items-baseline justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50" data-testid="plan-progress">
                {plan.paid_count} of {plan.cycles} instalments paid
              </span>
              <span className="font-mono text-xs text-terra">{money(plan.instalment_amount)}/mo</span>
            </div>
            <div className="mb-10 h-2 w-full bg-bone/10">
              <div className="h-2 bg-terra transition-all duration-700" style={{ width: `${(plan.paid_count / plan.cycles) * 100}%` }} />
            </div>

            {plan.status === "completed" ? (
              <div className="text-center" data-testid="plan-completed">
                <CheckCircle2 size={40} className="mx-auto mb-5 text-terra" />
                <h2 className="mb-3 font-serif text-2xl font-medium text-bone">All paid. Thank you!</h2>
                <p className="text-sm leading-relaxed text-bone/60">
                  Every instalment of {money(plan.total_amount)} has been collected. Zecharias will be in touch —
                  and it has been a pleasure working with you.
                </p>
              </div>
            ) : (
              <>
                <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50">Due now — instalment {plan.paid_count + 1} of {plan.cycles}</p>
                <p className="mb-8 font-serif text-5xl font-semibold tracking-tight text-bone" data-testid="plan-amount-due">
                  {money(plan.instalment_amount)}
                </p>

                {config && !config.configured ? (
                  <p className="text-sm text-terra" data-testid="plan-payments-unavailable">
                    Online payments are temporarily unavailable. Please reply to your reminder email and we
                    will send an alternative payment method.
                  </p>
                ) : (
                  <>
                    <div className="min-h-[112px] bg-bone p-4" ref={containerRef} data-testid="plan-paypal-buttons" />
                    {state === "loading" && (
                      <p className="mt-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-bone/40">
                        <Loader2 size={13} className="animate-spin" /> Loading PayPal…
                      </p>
                    )}
                    {state === "processing" && (
                      <p className="mt-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-terra" data-testid="plan-processing">
                        <Loader2 size={13} className="animate-spin" /> Confirming your payment…
                      </p>
                    )}
                    {state === "error" && (
                      <p className="mt-3 text-sm text-terra" data-testid="plan-error">
                        PayPal is unavailable right now. Please email Info@bookcarellc.com and we will help.
                      </p>
                    )}
                  </>
                )}

                <p className="mt-8 flex items-start gap-2 border-t border-bone/10 pt-5 font-mono text-[9px] uppercase leading-relaxed tracking-[0.15em] text-bone/40">
                  <ShieldCheck size={13} className="mt-px shrink-0 text-terra" />
                  Payments are processed by PayPal. Pay with a PayPal balance or any major card — we never see your card details.
                </p>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
